import { useState, useCallback } from 'react'
import { Annotation, AnnotationTool, AnnotationState, AnnotationActions } from '../components/annotations/types'

const INITIAL_STATE: AnnotationState = {
  annotations: [],
  currentTool: 'pen',
  selectedAnnotationId: null,
  toolColor: '#ef4444', // Red
  toolSize: 4, // Normal
  history: [[]],
  historyIndex: 0
}

export const useAnnotations = (): AnnotationState & AnnotationActions => {
  const [state, setState] = useState<AnnotationState>(INITIAL_STATE)

  const addToHistory = useCallback((annotations: Annotation[]) => {
    setState(prev => {
      const newHistory = prev.history.slice(0, prev.historyIndex + 1)
      newHistory.push([...annotations])
      
      // Limit history to 50 states to prevent memory issues
      if (newHistory.length > 50) {
        newHistory.shift()
      }
      
      return {
        ...prev,
        history: newHistory,
        historyIndex: newHistory.length - 1
      }
    })
  }, [])

  const addAnnotation = useCallback((annotation: Annotation) => {
    setState(prev => {
      const newAnnotations = [...prev.annotations, annotation]
      addToHistory(newAnnotations)
      
      return {
        ...prev,
        annotations: newAnnotations,
        selectedAnnotationId: annotation.id
      }
    })
  }, [addToHistory])

  const updateAnnotation = useCallback((id: string, updates: Partial<Annotation>) => {
    setState(prev => {
      const newAnnotations = prev.annotations.map(annotation =>
        annotation.id === id
          ? { ...annotation, ...updates, updatedAt: new Date().toISOString() }
          : annotation
      )
      
      // Don't add to history for every move operation (performance)
      // Only add when update includes significant changes
      if (updates.points || updates.text || updates.color || updates.size) {
        addToHistory(newAnnotations)
      }
      
      return {
        ...prev,
        annotations: newAnnotations
      }
    })
  }, [addToHistory])

  const deleteAnnotation = useCallback((id: string) => {
    setState(prev => {
      const newAnnotations = prev.annotations.filter(annotation => annotation.id !== id)
      addToHistory(newAnnotations)
      
      return {
        ...prev,
        annotations: newAnnotations,
        selectedAnnotationId: prev.selectedAnnotationId === id ? null : prev.selectedAnnotationId
      }
    })
  }, [addToHistory])

  const selectAnnotation = useCallback((id: string | null) => {
    setState(prev => ({
      ...prev,
      selectedAnnotationId: id,
      currentTool: id ? 'select' : prev.currentTool
    }))
  }, [])

  const setTool = useCallback((tool: AnnotationTool) => {
    setState(prev => ({
      ...prev,
      currentTool: tool,
      selectedAnnotationId: null // Deselect when changing tools
    }))
  }, [])

  const setToolColor = useCallback((color: string) => {
    setState(prev => ({
      ...prev,
      toolColor: color
    }))
  }, [])

  const setToolSize = useCallback((size: number) => {
    setState(prev => ({
      ...prev,
      toolSize: size
    }))
  }, [])

  const undo = useCallback(() => {
    setState(prev => {
      if (prev.historyIndex > 0) {
        const newIndex = prev.historyIndex - 1
        return {
          ...prev,
          annotations: [...prev.history[newIndex]],
          historyIndex: newIndex,
          selectedAnnotationId: null
        }
      }
      return prev
    })
  }, [])

  const redo = useCallback(() => {
    setState(prev => {
      if (prev.historyIndex < prev.history.length - 1) {
        const newIndex = prev.historyIndex + 1
        return {
          ...prev,
          annotations: [...prev.history[newIndex]],
          historyIndex: newIndex,
          selectedAnnotationId: null
        }
      }
      return prev
    })
  }, [])

  const clear = useCallback(() => {
    if (confirm('Are you sure you want to clear all annotations?')) {
      setState(prev => {
        addToHistory([])
        return {
          ...prev,
          annotations: [],
          selectedAnnotationId: null
        }
      })
    }
  }, [addToHistory])

  const exportAnnotations = useCallback((): Annotation[] => {
    return [...state.annotations]
  }, [state.annotations])

  const importAnnotations = useCallback((annotations: Annotation[]) => {
    setState(prev => {
      addToHistory(annotations)
      return {
        ...prev,
        annotations: [...annotations],
        selectedAnnotationId: null
      }
    })
  }, [addToHistory])

  return {
    ...state,
    addAnnotation,
    updateAnnotation,
    deleteAnnotation,
    selectAnnotation,
    setTool,
    setToolColor,
    setToolSize,
    undo,
    redo,
    clear,
    exportAnnotations,
    importAnnotations
  }
}

// Helper hook for undo/redo capabilities
export const useAnnotationHistory = (state: AnnotationState) => {
  return {
    canUndo: state.historyIndex > 0,
    canRedo: state.historyIndex < state.history.length - 1
  }
}